import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const CLV = () => {
  // State variables for CLV calculation
  const [avgPurchaseValue, setAvgPurchaseValue] = useState(0);
  const [purchaseFrequency, setPurchaseFrequency] = useState(0);
  const [customerLifespan, setCustomerLifespan] = useState(0);
  const [clv, setClv] = useState(0);

  // Event handlers to update state values and ensure inputs are valid numbers
  const handleAvgPurchaseValueChange = (e) =>
    setAvgPurchaseValue(parseFloat(e.target.value) || 0);
  const handlePurchaseFrequencyChange = (e) =>
    setPurchaseFrequency(parseFloat(e.target.value) || 0);
  const handleCustomerLifespanChange = (e) =>
    setCustomerLifespan(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate the CLV when inputs change
  useEffect(() => {
    if (avgPurchaseValue && purchaseFrequency && customerLifespan) {
      const calculatedCLV =
        avgPurchaseValue * purchaseFrequency * customerLifespan;
      setClv(calculatedCLV);
    } else {
      setClv(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [avgPurchaseValue, purchaseFrequency, customerLifespan]);

  return (
    <Layout title="Customer Lifetime Value (CLV) Calculator" path="clv">
      <section className="section-padding" id="clv-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* CLV Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Customer Lifetime Value (CLV) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Average Purchase Value Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="avgPurchaseValue" className="form-label">
                        Average Purchase Value (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Average Purchase Value:</strong> The average amount spent by a customer in one transaction."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="avgPurchaseValue"
                        placeholder="Enter average purchase value"
                        value={avgPurchaseValue || ""}
                        onChange={handleAvgPurchaseValueChange}
                      />
                    </div>

                    {/* Purchase Frequency Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="purchaseFrequency" className="form-label">
                        Purchase Frequency (per year)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Purchase Frequency:</strong> The average number of times a customer makes a purchase in a year."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="purchaseFrequency"
                        placeholder="Enter purchase frequency"
                        value={purchaseFrequency || ""}
                        onChange={handlePurchaseFrequencyChange}
                      />
                    </div>

                    {/* Customer Lifespan Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="customerLifespan" className="form-label">
                        Customer Lifespan (years)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Customer Lifespan:</strong> The average number of years a customer continues to purchase from your business."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="customerLifespan"
                        placeholder="Enter customer lifespan"
                        value={customerLifespan || ""}
                        onChange={handleCustomerLifespanChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Customer Lifetime Value (CLV):{" "}
                        <span className="fw-bold ms-3">
                          {clv === 0 ? "0.00" : clv.toFixed(2)} ₹
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Customer Lifetime Value (CLV) Calculator
                </h3>

                {/* What is a CLV Calculator? */}
                <h4 className="mt-3 mb-3">
                  What is a Customer Lifetime Value (CLV) Calculator?
                </h4>
                <p>
                  A **Customer Lifetime Value (CLV) Calculator** helps
                  businesses estimate the total revenue a customer is likely to
                  generate during their entire relationship with the company.
                  The CLV is calculated by multiplying the average purchase
                  value by the purchase frequency and the expected customer
                  lifespan.
                </p>

                {/* Why Use a CLV Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a CLV Calculator?</h4>
                <p>
                  This calculator is essential for understanding the long-term
                  value of a customer to a business. It helps businesses make
                  informed decisions about customer acquisition and retention
                  strategies, as well as guide marketing efforts and resource
                  allocation.
                </p>

                {/* Benefits of Using a CLV Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a CLV Calculator
                </h4>
                <ul>
                  <li>
                    <strong>
                      Informs Customer Acquisition and Retention Strategies:
                    </strong>{" "}
                    Helps businesses identify high-value customers and
                    prioritize retention efforts.
                  </li>
                  <li>
                    <strong>
                      Helps Businesses Allocate Marketing Budgets Efficiently:
                    </strong>{" "}
                    By understanding CLV, businesses can optimize their spending
                    on customer acquisition and retention.
                  </li>
                  <li>
                    <strong>Identifies High-Value Customers:</strong> CLV helps
                    businesses identify and focus on their most profitable
                    customers.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a CLV Calculator?</h4>
                <p>
                  The CLV calculator is ideal for businesses that rely on repeat
                  customers or subscriptions, including:
                </p>
                <ul>
                  <li>
                    <strong>Retail:</strong> Helps retailers understand the
                    value of each customer over time.
                  </li>
                  <li>
                    <strong>E-commerce:</strong> Assists online stores in
                    predicting customer lifetime value and planning marketing
                    strategies accordingly.
                  </li>
                  <li>
                    <strong>SaaS (Software as a Service):</strong> Helps
                    subscription-based services calculate and predict customer
                    value over time.
                  </li>
                  <li>
                    <strong>Subscription Services:</strong> Used to determine
                    customer worth in businesses that provide recurring products
                    or services.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">
                  How do I calculate Customer Lifetime Value?
                </h4>
                <p>
                  The formula for CLV is:{" "}
                  <strong>
                    CLV = (Average Purchase Value x Purchase Frequency) x
                    Customer Lifespan
                  </strong>
                  . This gives you an estimate of the total revenue you can
                  expect from a customer during their entire relationship with
                  your business.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is CLV important for businesses?
                </h4>
                <p>
                  CLV is crucial for businesses because it helps them understand
                  the long-term value of each customer. By knowing the CLV,
                  businesses can make better decisions on how much to invest in
                  acquiring and retaining customers, ensuring profitability and
                  growth.
                </p>

                <h4 className="mt-4 mb-3">
                  How can CLV help with marketing strategies?
                </h4>
                <p>
                  By understanding CLV, businesses can allocate their marketing
                  budgets more effectively, focusing on high-value customers and
                  implementing retention strategies that maximize long-term
                  revenue.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default CLV;
